import { api } from "@astro/api"
import { keepPreviousData, useQuery } from "@tanstack/react-query"
import { Link, useNavigate } from "react-router-dom"

/** Whole class names, because Tailwind only generates what it can read as a
 *  literal in the source — an interpolated `sm:grid-cols-${n}` is never built.
 *  (`sm:grid-cols-1` was genuinely missing from the bundle before this.) */
const CATEGORY_GRID_COLS = {
  1: "sm:grid-cols-1",
  2: "sm:grid-cols-2",
  3: "sm:grid-cols-3",
}

export default function ShopByCategorySection() {
  const navigate = useNavigate()

  const { data, isLoading } = useQuery({
    queryKey: ["allCategory"],
    queryFn: () => api.catalog.categories(),
    placeholderData: keepPreviousData
  });

  // Filter to only show top-level (parent) categories
  const categories = (data?.data || []).filter(cat => cat.parent_id === null);

  // Each top-level category has its own section further down this page
  // (e.g. #category-gemstone, #category-rudraksha) — scroll to it.
  // Categories without a section fall back to the category route.
  const goToCategory = (slug) => {
    const section = document.getElementById(`category-${slug}`)
    if (section) section.scrollIntoView({ behavior: 'smooth', block: 'start' })
    else navigate(`/${slug}`)
  }

  return (
    <section className="py-16 md:px-16 bg-white border-y border-gray-100">
      <div className="max-w-350 mx-auto px-4 md:px-6">
        <div className="mb-10 text-center md:text-left flex flex-col md:flex-row md:items-end justify-between">
          <div>
            <span className="text-[10px] tracking-[0.3em] font-bold text-secondary uppercase"><i className="fa-solid fa-table-cells-large mr-1"></i> DIVINE COLLECTIONS</span>
            <h2 className="font-heading text-3xl text-[#2B0B14] mt-1 uppercase font-normal">Shop By Category</h2>
          </div>
          <Link to="/products" className="text-xs font-bold tracking-widest text-[#2B0B14] hover:text-secondary transition uppercase mt-2 md:mt-0">All Products <i className="fa-solid fa-arrow-right ml-1"></i></Link>
        </div>

        {isLoading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            {[1, 2].map((i) => (
              <div key={i} className="rounded-3xl overflow-hidden border border-gray-100 bg-white animate-pulse">
                <div className="aspect-video w-full bg-gray-200" />
                <div className="p-5 flex justify-between items-center">
                  <div className="space-y-2">
                    <div className="h-5 w-32 bg-gray-200 rounded" />
                    <div className="h-3 w-24 bg-gray-100 rounded" />
                  </div>
                  <div className="w-10 h-10 rounded-full bg-gray-200" />
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className={`grid grid-cols-1 ${CATEGORY_GRID_COLS[Math.min(categories.length, 3)] || "sm:grid-cols-3"} gap-6`}>
            {categories.map((c) => (
              <button key={c.id} type="button" onClick={() => goToCategory(c.slug)} className="group relative rounded-3xl overflow-hidden shadow-sm hover:shadow-xl border border-gray-100 flex flex-col bg-white transition duration-500 cursor-pointer text-left">
                <div className="aspect-video w-full bg-amber-50/30 relative overflow-hidden">
                  <img src={c.image_url} alt={c.name} className="w-full h-full object-fill transition-transform duration-700 group-hover:scale-105" />
                </div>
                <div className="p-5 flex justify-between items-center bg-white">
                  <div><h3 className="font-heading text-xl text-[#2B0B14]">{c.name}</h3><p className="text-[9px] font-semibold tracking-wider text-gray-400 uppercase">Explore Collection</p></div>
                  <div className="w-10 h-10 rounded-full border border-amber-200 flex items-center justify-center group-hover:bg-[#2B0B14] group-hover:text-white transition"><i className="fa-solid fa-arrow-down"></i></div>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
    </section>
  )
}
